/*  Preview delle 3 combo per le card "Media per Tecnico" / "Media per Hotel"
    Route: /mockup/v2/cards-preview
    Tutto in tema Data Intel (Proposta C).
*/

import React from 'react';
import { Link } from 'react-router-dom';
import { useV2Fonts } from './_shared';
import { PROPOSALS } from './_mobileShared';
import { Wrench, Building2, TrendingUp, TrendingDown, Award, Zap, Activity, AlertTriangle, ChevronRight, Sparkles } from 'lucide-react';

const C = PROPOSALS.c;

/* --- Mock data condivisi --- */
const TECHS = [
  { name: 'M. Rossi', short: 'MR', closed7d: 12, avgTime: '1h 45m', open: 4, badge: 'top', trend: [1, 2, 1, 3, 2, 2, 1] },
  { name: 'L. Bianchi', short: 'LB', closed7d: 9, avgTime: '2h 10m', open: 3, badge: 'fast', trend: [0, 1, 2, 1, 2, 2, 1] },
  { name: 'D. Conti', short: 'DC', closed7d: 8, avgTime: '2h 30m', open: 5, badge: null, trend: [1, 1, 1, 2, 1, 1, 1] },
  { name: 'A. Costa', short: 'AC', closed7d: 6, avgTime: '3h 15m', open: 3, badge: null, trend: [0, 0, 1, 1, 2, 1, 1] },
  { name: 'P. Neri', short: 'PN', closed7d: 4, avgTime: '4h 00m', open: 7, badge: 'slow', trend: [0, 0, 1, 0, 1, 1, 1] },
  { name: 'R. Marini', short: 'RM', closed7d: 3, avgTime: '4h 30m', open: 8, badge: 'slow', trend: [0, 0, 0, 1, 0, 1, 1] },
];

const HOTELS = [
  { code: 'GAL', name: 'Hotel Galleria', open: 12, critical: 3, sla: 98, eff: 67, color: '#3B82F6', trend7d: [10, 12, 11, 13, 12, 12, 12], status: 'good' },
  { code: 'ARC', name: 'Hotel Arcimboldi', open: 9, critical: 2, sla: 96, eff: 71, color: '#A855F7', trend7d: [8, 9, 10, 9, 9, 8, 9], status: 'good' },
  { code: 'MNZ', name: 'Hotel Manzoni', open: 14, critical: 5, sla: 89, eff: 58, color: '#F59E0B', trend7d: [10, 12, 14, 15, 14, 13, 14], status: 'warning' },
  { code: 'KIR', name: 'Hotel Kira', open: 10, critical: 2, sla: 95, eff: 63, color: '#10B981', trend7d: [11, 9, 10, 10, 11, 10, 10], status: 'good' },
];

/* --- Helpers --- */
const Card = ({ children, className = '', padding = 'p-5', label, title }) => (
  <div
    className={`rounded-lg ${padding} ${className}`}
    style={{ background: C.surface, border: `1px solid ${C.border}` }}
  >
    {(label || title) && (
      <div className="mb-4">
        {label && (
          <div className="text-[10px] tracking-widest" style={{ color: C.mutedDim, fontFamily: C.fontMono }}>
            {label}
          </div>
        )}
        {title && (
          <h2 className="text-sm font-semibold mt-0.5" style={{ fontFamily: C.fontDisplay }}>
            {title}
          </h2>
        )}
      </div>
    )}
    {children}
  </div>
);

const Sparkline = ({ data, color = C.accent, height = 22, width = 70, fill = true }) => {
  const max = Math.max(...data, 1);
  const min = Math.min(...data);
  const range = max - min || 1;
  const step = width / (data.length - 1);
  const pts = data.map((v, i) => `${i * step},${height - ((v - min) / range) * (height - 4) - 2}`).join(' ');
  const lastY = height - ((data[data.length - 1] - min) / range) * (height - 4) - 2;
  return (
    <svg width={width} height={height} className="overflow-visible">
      {fill && (
        <>
          <defs>
            <linearGradient id={`grad-${color.replace('#', '')}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity="0.4" />
              <stop offset="100%" stopColor={color} stopOpacity="0" />
            </linearGradient>
          </defs>
          <polygon fill={`url(#grad-${color.replace('#', '')})`} points={`0,${height} ${pts} ${width},${height}`} />
        </>
      )}
      <polyline fill="none" stroke={color} strokeWidth="1.5" points={pts} style={{ filter: `drop-shadow(0 0 3px ${color})` }} />
      <circle cx={width} cy={lastY} r="2" fill={color} />
    </svg>
  );
};

/* ============================================================
   COMBO 1 — Operatività Massima (Leaderboard + Cards 2x2)
   ============================================================ */
const Combo1 = () => {
  const sortedTechs = [...TECHS].sort((a, b) => b.closed7d - a.closed7d).slice(0, 5);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* Tecnici — Leaderboard */}
      <Card label="MODULE_01 · TEAM_LEADERBOARD" title="Top Tecnici · ultimi 7 giorni">
        <div className="space-y-2.5">
          {sortedTechs.map((t, i) => {
            const isTop = i === 0;
            const badgeMap = {
              top: { label: 'TOP', color: C.success, icon: Award },
              fast: { label: 'VELOCE', color: C.accent, icon: Zap },
              slow: { label: 'IN RITARDO', color: C.warning, icon: AlertTriangle },
            };
            const b = t.badge ? badgeMap[t.badge] : null;
            return (
              <div
                key={t.name}
                className="flex items-center gap-3 p-2.5 rounded-md"
                style={{
                  background: isTop ? 'rgba(16,185,129,0.06)' : 'transparent',
                  border: `1px solid ${isTop ? 'rgba(16,185,129,0.25)' : C.border}`,
                }}
              >
                {/* Rank */}
                <div
                  className="w-7 h-7 rounded flex items-center justify-center text-xs font-bold tabular-nums"
                  style={{
                    background: isTop ? 'rgba(16,185,129,0.18)' : C.surface2,
                    color: isTop ? C.success : C.muted,
                    fontFamily: C.fontDisplay,
                  }}
                >
                  {i + 1}
                </div>
                {/* Name + badge */}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium flex items-center gap-2">
                    <span>{t.name}</span>
                    {b && (
                      <span
                        className="text-[9px] px-1.5 py-0.5 rounded font-bold tracking-widest flex items-center gap-1"
                        style={{ background: `${b.color}22`, color: b.color, fontFamily: C.fontMono }}
                      >
                        <b.icon className="w-2.5 h-2.5" />
                        {b.label}
                      </span>
                    )}
                  </div>
                  <div className="text-[10px] mt-0.5 flex items-center gap-2" style={{ color: C.muted, fontFamily: C.fontMono }}>
                    <span>⏱ {t.avgTime}</span>
                    <span>· {t.open} aperti</span>
                  </div>
                </div>
                {/* Closed count */}
                <div className="text-right">
                  <div className="text-2xl font-bold tabular-nums leading-none" style={{ color: isTop ? C.success : C.text, fontFamily: C.fontDisplay }}>
                    {t.closed7d}
                  </div>
                  <div className="text-[9px]" style={{ color: C.mutedDim, fontFamily: C.fontMono }}>CHIUSI 7D</div>
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Hotel — Cards 2x2 */}
      <Card label="MODULE_02 · MULTI_PROPERTY" title="Stato Strutture">
        <div className="grid grid-cols-2 gap-2.5">
          {HOTELS.map((h) => {
            const statusColor = h.status === 'warning' ? C.warning : C.success;
            return (
              <div
                key={h.code}
                className="p-3 rounded-md"
                style={{ background: C.surface2, border: `1px solid ${C.border}` }}
              >
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <div className="text-[9px] tracking-widest" style={{ color: C.mutedDim, fontFamily: C.fontMono }}>
                      {h.code}
                    </div>
                    <div className="text-sm font-semibold leading-tight" style={{ fontFamily: C.fontDisplay }}>
                      {h.name.replace('Hotel ', '')}
                    </div>
                  </div>
                  <span
                    className="w-1.5 h-1.5 rounded-full"
                    style={{ background: statusColor, boxShadow: `0 0 8px ${statusColor}` }}
                  />
                </div>
                <div className="grid grid-cols-2 gap-1.5 mt-3">
                  <div>
                    <div className="text-[9px]" style={{ color: C.mutedDim, fontFamily: C.fontMono }}>APERTI</div>
                    <div className="text-xl font-bold tabular-nums" style={{ fontFamily: C.fontDisplay }}>{h.open}</div>
                  </div>
                  <div>
                    <div className="text-[9px]" style={{ color: C.mutedDim, fontFamily: C.fontMono }}>CRITICI</div>
                    <div
                      className="text-xl font-bold tabular-nums"
                      style={{ color: h.critical >= 3 ? C.danger : C.text, fontFamily: C.fontDisplay }}
                    >
                      {h.critical}
                    </div>
                  </div>
                  <div>
                    <div className="text-[9px]" style={{ color: C.mutedDim, fontFamily: C.fontMono }}>SLA</div>
                    <div
                      className="text-sm font-bold tabular-nums"
                      style={{ color: h.sla >= 95 ? C.success : C.warning, fontFamily: C.fontDisplay }}
                    >
                      {h.sla}%
                    </div>
                  </div>
                  <div>
                    <div className="text-[9px]" style={{ color: C.mutedDim, fontFamily: C.fontMono }}>EFF.</div>
                    <div
                      className="text-sm font-bold tabular-nums"
                      style={{ color: h.eff >= 65 ? C.success : C.warning, fontFamily: C.fontDisplay }}
                    >
                      {h.eff}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
};

/* ============================================================
   COMBO 2 — Visualizzazione Viva (Sparkline list + Sparkline list)
   ============================================================ */
const Combo2 = () => (
  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
    {/* Tecnici — sparkline list */}
    <Card label="MODULE_03 · TECH_TREND_7D" title="Trend Tecnici · 7 giorni">
      <div className="space-y-3">
        {TECHS.slice(0, 6).map((t) => {
          const overload = t.open >= 7;
          return (
            <div key={t.name} className="flex items-center gap-3">
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-bold"
                style={{ background: C.surface2, color: C.muted, fontFamily: C.fontMono }}
              >
                {t.short}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium truncate">{t.name}</span>
                  <span className="text-[10px]" style={{ color: C.muted, fontFamily: C.fontMono }}>
                    <span style={{ color: overload ? C.danger : C.muted }}>{t.open} aperti</span> · {t.closed7d} chiusi
                  </span>
                </div>
                <div className="mt-1 flex items-center justify-between">
                  <Sparkline data={t.trend} color={overload ? C.warning : C.accent} width={140} />
                  <span className="text-[10px]" style={{ color: C.mutedDim, fontFamily: C.fontMono }}>⏱ {t.avgTime}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </Card>

    {/* Hotel — sparkline list */}
    <Card label="MODULE_04 · HOTEL_TREND_7D" title="Stato Strutture · 7 giorni">
      <div className="space-y-3">
        {HOTELS.map((h) => {
          const dotColor = h.status === 'warning' ? C.warning : C.success;
          return (
            <div key={h.code} className="flex items-center gap-3">
              <span
                className="w-1.5 h-8 rounded-full"
                style={{ background: h.color, boxShadow: `0 0 6px ${h.color}99` }}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{h.name}</span>
                    <span
                      className="w-1.5 h-1.5 rounded-full"
                      style={{ background: dotColor, boxShadow: `0 0 6px ${dotColor}` }}
                    />
                  </div>
                  <span className="text-[10px]" style={{ color: C.muted, fontFamily: C.fontMono }}>
                    <span style={{ color: h.critical >= 3 ? C.danger : C.muted }}>{h.critical} critici</span>
                  </span>
                </div>
                <div className="mt-1 flex items-center justify-between">
                  <Sparkline data={h.trend7d} color={h.color} width={140} />
                  <span className="tabular-nums text-sm font-semibold" style={{ fontFamily: C.fontDisplay }}>
                    {h.open}
                    <span className="text-[10px] font-normal" style={{ color: C.mutedDim, fontFamily: C.fontMono }}> aperti</span>
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  </div>
);

/* ============================================================
   COMBO 3 — Bloomberg-style (Heatmap + Stacked Bar)
   ============================================================ */
const Combo3 = () => {
  const heatColor = (v) => {
    if (v === 0) return { bg: 'rgba(148,163,184,0.08)', border: 'rgba(148,163,184,0.2)' };
    if (v <= 1) return { bg: 'rgba(59,130,246,0.25)', border: 'rgba(59,130,246,0.5)' };
    if (v <= 2) return { bg: 'rgba(16,185,129,0.45)', border: '#10B981' };
    return { bg: 'rgba(16,185,129,0.8)', border: '#10B981' };
  };
  const days = ['L', 'M', 'M', 'G', 'V', 'S', 'D'];

  // mock: stato breakdown per hotel
  const HOTEL_BREAKDOWN = HOTELS.map((h) => ({
    ...h,
    states: { open: Math.max(0, h.open - h.critical - 3), in_progress: 2, waiting: 1, critical: h.critical },
  }));

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* Tecnici — Heatmap GitHub-style */}
      <Card label="MODULE_05 · TECH_ACTIVITY_HEATMAP" title="Attività Tecnici · ultima settimana">
        <div className="grid" style={{ gridTemplateColumns: '70px repeat(7, 1fr) 50px', gap: 4 }}>
          {/* Header days */}
          <div />
          {days.map((d, i) => (
            <div key={i} className="text-center text-[10px]" style={{ color: C.mutedDim, fontFamily: C.fontMono }}>{d}</div>
          ))}
          <div className="text-right text-[10px]" style={{ color: C.mutedDim, fontFamily: C.fontMono }}>TOT</div>

          {/* Rows */}
          {TECHS.map((t) => (
            <React.Fragment key={t.name}>
              <div className="text-xs font-medium flex items-center truncate" style={{ color: C.muted, fontFamily: C.fontMono }}>
                {t.name}
              </div>
              {t.trend.map((v, i) => {
                const hc = heatColor(v);
                return (
                  <div
                    key={i}
                    className="aspect-square rounded flex items-center justify-center text-[10px] font-bold tabular-nums"
                    style={{
                      background: hc.bg,
                      border: `1px solid ${hc.border}`,
                      color: v > 0 ? C.text : C.mutedDim,
                      fontFamily: C.fontMono,
                    }}
                  >
                    {v || ''}
                  </div>
                );
              })}
              <div className="text-right text-sm font-bold tabular-nums flex items-center justify-end" style={{ fontFamily: C.fontDisplay }}>
                {t.closed7d}
              </div>
            </React.Fragment>
          ))}
        </div>
        <div className="mt-4 flex items-center gap-2 text-[10px]" style={{ color: C.mutedDim, fontFamily: C.fontMono }}>
          <span>MENO</span>
          {[0, 1, 2, 3].map((v) => {
            const hc = heatColor(v);
            return <div key={v} className="w-3.5 h-3.5 rounded" style={{ background: hc.bg, border: `1px solid ${hc.border}` }} />;
          })}
          <span>PIÙ</span>
          <span className="ml-auto">{TECHS.reduce((s, t) => s + t.closed7d, 0)} ticket totali</span>
        </div>
      </Card>

      {/* Hotel — Stacked bar per stato */}
      <Card label="MODULE_06 · HOTEL_STATUS_MIX" title="Mix ticket per stato · per hotel">
        <div className="space-y-3.5">
          {HOTEL_BREAKDOWN.map((h) => {
            const total = h.states.open + h.states.in_progress + h.states.waiting + h.states.critical;
            return (
              <div key={h.code}>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full" style={{ background: h.color, boxShadow: `0 0 6px ${h.color}55` }} />
                    <span className="text-sm font-medium">{h.name}</span>
                  </div>
                  <span className="text-sm font-bold tabular-nums" style={{ fontFamily: C.fontDisplay }}>{total}</span>
                </div>
                <div className="h-3 rounded-full overflow-hidden flex" style={{ background: C.surface2 }}>
                  <div
                    style={{
                      width: `${(h.states.open / total) * 100}%`,
                      background: '#94A3B8',
                    }}
                    title={`Aperti: ${h.states.open}`}
                  />
                  <div
                    style={{
                      width: `${(h.states.in_progress / total) * 100}%`,
                      background: C.accent,
                    }}
                    title={`In lavoro: ${h.states.in_progress}`}
                  />
                  <div
                    style={{
                      width: `${(h.states.waiting / total) * 100}%`,
                      background: '#A855F7',
                    }}
                    title={`In attesa: ${h.states.waiting}`}
                  />
                  <div
                    style={{
                      width: `${(h.states.critical / total) * 100}%`,
                      background: C.danger,
                      boxShadow: `inset 0 0 4px ${C.danger}`,
                    }}
                    title={`Critici: ${h.states.critical}`}
                  />
                </div>
                <div className="mt-1 flex items-center gap-3 text-[10px]" style={{ fontFamily: C.fontMono }}>
                  <span style={{ color: '#94A3B8' }}>● {h.states.open} aperti</span>
                  <span style={{ color: C.accent }}>● {h.states.in_progress} in lavoro</span>
                  <span style={{ color: '#A855F7' }}>● {h.states.waiting} attesa</span>
                  <span style={{ color: C.danger }}>● {h.states.critical} critici</span>
                </div>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
};

/* ============================================================
   PAGE
   ============================================================ */

const SectionTitle = ({ num, name, desc }) => (
  <div className="mb-3 flex items-end justify-between">
    <div>
      <div className="text-[10px] tracking-widest" style={{ color: C.mutedDim, fontFamily: C.fontMono }}>
        COMBO_{num}
      </div>
      <h2 className="text-2xl font-bold" style={{ fontFamily: C.fontDisplay }}>{name}</h2>
      <p className="text-sm mt-1" style={{ color: C.muted }}>{desc}</p>
    </div>
  </div>
);

const CardsPreview = () => {
  useV2Fonts();

  return (
    <div
      className="min-h-screen"
      style={{ background: C.bg, fontFamily: C.fontBody, color: C.text }}
      data-testid="cards-preview"
    >
      <header className="border-b sticky top-0 z-10" style={{ borderColor: C.border, background: C.bg }}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] tracking-widest" style={{ color: C.mutedDim, fontFamily: C.fontMono }}>
              DASHBOARD · MEDIA TECNICI/HOTEL · PREVIEW
            </div>
            <h1 className="text-xl font-bold" style={{ fontFamily: C.fontDisplay }}>3 alternative a confronto</h1>
          </div>
          <Link
            to="/mockup/v2"
            className="text-xs hover:text-white"
            style={{ color: C.muted, fontFamily: C.fontMono }}
            data-testid="back-to-v2-index"
          >
            ← Back
          </Link>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-12">
        <section>
          <SectionTitle num="01" name="Operatività Massima" desc="Leaderboard tecnici (chi sta lavorando bene) + Hotel cards 2×2 (stato di ogni struttura)." />
          <Combo1 />
        </section>

        <div className="border-t" style={{ borderColor: C.border }} />

        <section>
          <SectionTitle num="02" name="Visualizzazione Viva" desc="Lista compatta come l'attuale ma con sparkline 7gg invece dei numeri 0. Mantiene il layout familiare." />
          <Combo2 />
        </section>

        <div className="border-t" style={{ borderColor: C.border }} />

        <section>
          <SectionTitle num="03" name="Bloomberg-style" desc="Heatmap GitHub per tecnici (pattern spotting) + Stacked bar per hotel (mix stati ticket)." />
          <Combo3 />
        </section>

        <div className="text-center pt-8 pb-4">
          <p className="text-sm" style={{ color: C.muted }}>
            Una volta scelto, scrivimi <span className="font-mono" style={{ color: C.accent }}>&quot;Combo 1&quot;</span>,{' '}
            <span className="font-mono" style={{ color: C.accent }}>&quot;Combo 2&quot;</span> o{' '}
            <span className="font-mono" style={{ color: C.accent }}>&quot;Combo 3&quot;</span> e lo applico nella Dashboard live.
          </p>
        </div>
      </main>
    </div>
  );
};

export default CardsPreview;
